import { motion } from 'framer-motion';
import { ArrowRight, Brain, Fingerprint, HeartHandshake, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Eyebrow } from '../components/marketing/Eyebrow';
import { Hero } from '../components/marketing/Hero';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { staggerContainer, staggerItem } from '../components/ui/motionVariants';
import { formatLkr } from '../lib/format';
import { usePackages } from '../lib/hooks/usePackages';

const cardEntrance = {
  hidden: { opacity: 0, y: 20, scale: 0.98 },
  show: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { type: 'spring' as const, stiffness: 300, damping: 24 },
  },
};

const TIER_ACCENTS = [
  'from-primary to-accent',
  'from-accent to-primary',
  'from-primary via-accent to-primary',
];

const PILLARS = [
  {
    icon: Brain,
    title: 'Neuroplasticity & Epigenetics',
    description:
      'Rewire the neural pathways behind old, limiting patterns and build new, successful behaviors from the ground up.',
  },
  {
    icon: Sparkles,
    title: 'RAS Activation',
    description:
      "Activate your brain's natural filter so your attention automatically gravitates toward success, opportunity, and growth.",
  },
  {
    icon: HeartHandshake,
    title: 'Positive Psychology & NLP',
    description:
      'Harness the innate abilities already within you, using Neuro-Linguistic Programming to unlock maximum potential.',
  },
];

const STEPS = [
  {
    step: '01',
    title: 'DMIT Fingerprint Scan',
    description:
      'A biometric scan of your fingerprints reveals your inborn potential and personality traits — no guesswork, no assumptions.',
  },
  {
    step: '02',
    title: 'Your Personalised Report',
    description:
      'Receive a deep, data-grounded report on your natural strengths, dominant intelligences, and learning style.',
  },
  {
    step: '03',
    title: 'Coaching, Healing & Review',
    description:
      'Work through a structured program of coaching and healing sessions built around what your report reveals about you.',
  },
];

export function HomePage(): JSX.Element {
  const { data: packages } = usePackages();
  const featured = (packages ?? []).slice(0, 3);

  return (
    <div className="flex flex-col">
      {/* Hero */}
      <Hero
        eyebrow="Sanath Gamage Coaching · Life Re-Engineering"
        title="Rebuild your life on a fresh, scientific foundation."
        description="Inner Re-Engineering uncovers the negative patterns and limiting beliefs holding you back, and replaces them with a new foundation for success — grounded in neuroplasticity, biometric science, and NLP."
        image="https://images.unsplash.com/photo-1772041045313-0500d3706819?auto=format&fit=crop&w=1920&q=80"
        align="left"
      >
        <Link to="/packages">
          <Button size="lg" trailingIcon={<ArrowRight className="w-4 h-4" />}>
            Explore Packages
          </Button>
        </Link>
        <Link to="/about">
          <Button size="lg" variant="secondary">
            Learn About the Program
          </Button>
        </Link>
      </Hero>

      {/* How it works */}
      <section className="max-w-6xl mx-auto px-6 py-20">
        <motion.div
          variants={staggerContainer()}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: '-80px' }}
          className="flex flex-col gap-3 mb-12 text-center max-w-2xl mx-auto"
        >
          <motion.div variants={staggerItem}>
            <Eyebrow center>How it works</Eyebrow>
          </motion.div>
          <motion.h2
            variants={staggerItem}
            className="text-3xl sm:text-4xl font-display font-semibold text-foreground"
          >
            From fingerprint scan to lasting transformation
          </motion.h2>
        </motion.div>
        <motion.div
          variants={staggerContainer()}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: '-80px' }}
          className="grid gap-6 sm:grid-cols-3"
        >
          {STEPS.map((s) => (
            <motion.div key={s.step} variants={staggerItem}>
              <Card padded className="h-full flex flex-col gap-3">
                <span className="text-3xl font-display font-medium text-primary/30">{s.step}</span>
                <h3 className="text-lg font-display font-semibold text-foreground">{s.title}</h3>
                <p className="text-sm text-muted-foreground">{s.description}</p>
              </Card>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* Reflective process */}
      <section className="max-w-6xl mx-auto px-6 pb-20">
        <motion.div
          variants={staggerContainer()}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: '-80px' }}
          className="grid gap-10 lg:grid-cols-2 lg:items-center"
        >
          <motion.div
            variants={staggerItem}
            className="rounded-3xl overflow-hidden aspect-[4/3] bg-cover bg-center"
            style={{
              backgroundImage:
                "url('https://images.unsplash.com/photo-1484480974693-6ca0a78fb36b?auto=format&fit=crop&w=1200&q=80')",
            }}
          />
          <div className="flex flex-col gap-4">
            <motion.div variants={staggerItem}>
              <Eyebrow>Your journey, documented</Eyebrow>
            </motion.div>
            <motion.h2
              variants={staggerItem}
              className="text-3xl sm:text-4xl font-display font-semibold text-foreground"
            >
              A record of who you&rsquo;re becoming
            </motion.h2>
            <motion.p variants={staggerItem} className="text-muted-foreground leading-relaxed">
              Every session, every insight, and every shift in behavior is captured in your report —
              so progress isn&rsquo;t just a feeling, it&rsquo;s something you can look back on and
              see, in writing, month after month.
            </motion.p>
          </div>
        </motion.div>
      </section>

      {/* 3 core pillars */}
      <section className="bg-secondary/40">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <motion.div
            variants={staggerContainer()}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: '-80px' }}
            className="flex flex-col gap-3 mb-12 text-center max-w-2xl mx-auto"
          >
            <motion.div variants={staggerItem}>
              <Eyebrow center>The science behind it</Eyebrow>
            </motion.div>
            <motion.h2
              variants={staggerItem}
              className="text-3xl sm:text-4xl font-display font-semibold text-foreground"
            >
              3 core scientific pillars
            </motion.h2>
          </motion.div>
          <motion.div
            variants={staggerContainer()}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: '-80px' }}
            className="grid gap-6 sm:grid-cols-3"
          >
            {PILLARS.map((p) => (
              <motion.div key={p.title} variants={staggerItem}>
                <Card padded className="h-full flex flex-col gap-3" interactive>
                  <div className="w-11 h-11 rounded-2xl bg-primary/10 text-primary flex items-center justify-center">
                    <p.icon className="w-5 h-5" />
                  </div>
                  <h3 className="text-lg font-display font-semibold text-foreground">{p.title}</h3>
                  <p className="text-sm text-muted-foreground">{p.description}</p>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Editorial break */}
      <motion.section
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, margin: '-80px' }}
        transition={{ duration: 0.8 }}
        className="relative overflow-hidden bg-surface-ink text-white min-h-[420px] flex items-center"
      >
        <div
          className="absolute inset-0 bg-cover bg-center opacity-70"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1506748686214-e9df14d4d9d0?auto=format&fit=crop&w=1920&q=80')",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-surface-ink via-surface-ink/60 to-surface-ink/30" />
        <p className="relative max-w-3xl mx-auto px-6 text-center text-3xl sm:text-4xl lg:text-5xl font-display font-semibold leading-tight">
          The version of you that already exists — waiting to be uncovered.
        </p>
      </motion.section>

      {/* Packages teaser */}
      {featured.length > 0 && (
        <section className="max-w-6xl mx-auto px-6 py-20">
          <div className="flex items-end justify-between gap-4 mb-10 flex-wrap">
            <div className="flex flex-col gap-2">
              <Eyebrow>Choose your path</Eyebrow>
              <h2 className="text-3xl sm:text-4xl font-display font-semibold text-foreground">
                Programs for every stage of transformation
              </h2>
            </div>
            <Link
              to="/packages"
              className="text-sm font-medium text-primary hover:text-primary/80 inline-flex items-center gap-1"
            >
              View all packages <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <motion.div
            variants={staggerContainer()}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: '-80px' }}
            className="grid gap-6 sm:grid-cols-3"
          >
            {featured.map((pkg, i) => (
              <motion.div key={pkg.id} variants={cardEntrance}>
                <Card padded={false} className="h-full flex flex-col" interactive>
                  <div
                    className={`h-1 w-full bg-gradient-to-r ${TIER_ACCENTS[i % TIER_ACCENTS.length]}`}
                  />
                  <div className="p-5 flex flex-col gap-4 flex-1">
                    <div className="flex items-center gap-2 text-primary">
                      <Fingerprint className="w-5 h-5" />
                      <span className="text-xs font-semibold uppercase tracking-wide">
                        Tier {pkg.tier}
                      </span>
                    </div>
                    <h3 className="text-lg font-display font-semibold text-foreground">
                      {pkg.name}
                    </h3>
                    <p className="text-2xl font-display font-medium text-foreground">
                      {formatLkr(pkg.priceLkr)}
                    </p>
                    <p className="text-sm text-muted-foreground line-clamp-3">{pkg.description}</p>
                  </div>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </section>
      )}

      {/* Coach teaser */}
      <section className="bg-secondary/40">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.5 }}
          className="max-w-6xl mx-auto px-6 py-14 flex flex-col sm:flex-row items-center gap-6 text-center sm:text-left"
        >
          <img
            src="/images/expert-portrait.png"
            alt="Sanath Gamage"
            className="w-20 h-20 rounded-full object-cover ring-4 ring-background shadow-soft shrink-0"
          />
          <p className="text-foreground text-lg font-display leading-snug flex-1">
            &ldquo;The best path for each of us is already written, biologically, in our
            fingerprints. My work is helping you read it.&rdquo;
            <span className="block mt-1 text-sm text-muted-foreground font-sans">
              Sanath Gamage
            </span>
          </p>
          <Link
            to="/about"
            className="text-sm font-medium text-primary hover:text-primary/80 inline-flex items-center gap-1 shrink-0"
          >
            Meet Sanath <ArrowRight className="w-4 h-4" />
          </Link>
        </motion.div>
      </section>

      {/* CTA */}
      <section className="bg-surface-ink text-white">
        <div className="max-w-6xl mx-auto px-6 py-16 flex flex-col sm:flex-row items-center justify-between gap-6 text-center sm:text-left">
          <div>
            <h2 className="text-3xl sm:text-4xl font-display font-semibold text-white">
              Ready to start your transformation?
            </h2>
            <p className="text-white/70 mt-1">
              Reach out and we&rsquo;ll help you find the right program to begin.
            </p>
          </div>
          <Link to="/contact">
            <Button size="lg" trailingIcon={<ArrowRight className="w-4 h-4" />}>
              Get In Touch
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
